<template>
  <div
    class="cascading-menu"
    @mouseenter="handleMenuEnter"
    @mouseleave="handleMenuLeave">
    <ul class="menu-list">
      <li
        v-for="(item, index) in items"
        :key="index"
        class="menu-item"
        @mouseenter="openSubmenu(index, item, $event)"
        @mouseleave="closeSubmenu(index)">

        <button
          @click="handleClick(item)"
          :class="{'disabled': item.disabled, 'has-submenu': item.submenu}"
          class="menu-button">
          <i class="material-symbols-outlined">{{ item.icon }}</i>
          <span>{{ item.label }}</span>
          <i v-if="item.submenu" class="material-symbols-outlined submenu-icon">chevron_right</i>
        </button>

        <!-- Submenu for items that have one -->
        <div
          v-if="item.submenu && openSubmenus[index] && openSubmenus[index].isOpen"
          class="submenu-container"
          :style="openSubmenus[index].style">
          <CascadingMenu
            :menuId="menuId + '-sub-' + index"
            :items="item.submenu"
            @submenu-action="$emit('submenu-action', $event)"/>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { eventBus } from '@/_helpers/eventBus.js'

export default {
  name: 'CascadingMenu',
  props: {
    menuId: { type: String, required: true }, // Unique ID for each menu
    items: { type: Array, required: true },
    menuStyle: { type: Object, default: () => ({}) }
  },
  data () {
    return {
      openSubmenus: {}, // Tracks which submenus are open
      hoverTimeout: null,
      activeSubmenu: null,
      submenuPosition: { top: 0, left: 0 },
      isDragging: false
    }
  },
  created () {
    eventBus.$on('close-all-menus', this.handleCloseAllMenus)
    // Listen for drag events
    eventBus.$on('dragging-started', this.handleDragStart)
    eventBus.$on('dragging-ended', this.handleDragEnd)
  },
  beforeDestroy () {
    eventBus.$off('close-all-menus', this.handleCloseAllMenus)
    clearTimeout(this.hoverTimeout)
    // Clean up event listeners
    eventBus.$off('dragging-started', this.handleDragStart)
    eventBus.$off('dragging-ended', this.handleDragEnd)
  },
  methods: {
    openSubmenu (index, item, event) {
      // Don't open submenus if dragging
      if (this.isDragging || !item.submenu) return

      eventBus.$emit('menu-item-hover', { menuId: this.menuId, isHovering: true })

      // Clear any existing timeout to prevent flickering
      clearTimeout(this.hoverTimeout)

      // Find the closest menu-item element to get the correct position
      const parentItem = event.target.closest('.menu-item')
      if (!parentItem) return

      const rect = parentItem.getBoundingClientRect()

      this.$set(this.openSubmenus, index, {
        isOpen: true,
        items: item.submenu,
        style: {
          position: 'fixed', // Changed to fixed positioning
          top: `${rect.top}px`,
          left: `${rect.right + 5}px`,
          zIndex: 1001
        }
      })
    },

    closeSubmenu (index) {
      // Use a timeout to allow moving to submenu
      this.hoverTimeout = setTimeout(() => {
        this.$set(this.openSubmenus, index, { isOpen: false, items: [], style: {} })
      }, 100) // Short delay to allow mouse movement to submenu
    },

    handleClick (item) {
      if (item.disabled) return // Don't trigger action for disabled items
      this.$emit('submenu-action', item.action)
    },

    handleMenuEnter () {
      this.$emit('menu-hover', true)
      eventBus.$emit('menu-item-hover', { menuId: this.menuId, isHovering: true })
    },

    handleMenuLeave (event) {
      this.$emit('menu-hover', false)
      eventBus.$emit('menu-item-hover', { menuId: this.menuId, isHovering: false })
    },

    handleCloseAllMenus (keepOpenId) {
      if (this.menuId !== keepOpenId) {
        // Close all submenus
        this.openSubmenus = {}
      }
    },

    handleDragStart () {
      this.isDragging = true
      this.closeAllSubmenus()
      // Emit event to ensure parent components are aware
      this.$emit('menu-hover', false)
      eventBus.$emit('menu-item-hover', { menuId: this.menuId, isHovering: false })
    },

    handleDragEnd () {
      this.isDragging = false
    },

    closeAllSubmenus () {
      // Close all submenus immediately
      Object.keys(this.openSubmenus).forEach(key => {
        this.$set(this.openSubmenus, key, { isOpen: false, items: [], style: {} })
      })
      this.activeSubmenu = null
      clearTimeout(this.hoverTimeout)
    }
  }
}
</script>

<style scoped lang='scss'>
.cascading-menu {
  background: white;
  border: 1px solid #eaeaea;
  border-radius: 4px;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.15);
  min-width: 220px;
  overflow: visible; // Changed from hidden to allow submenus to overflow
}

.menu-list {
  list-style: none;
  margin: 0;
  padding: 6px;
  display: flex;
  flex-direction: column;
}

.menu-item {
  margin-bottom: 2px;
  position: relative; // Important for submenu positioning
}

.menu-button {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px;
  border: none;
  background: transparent;
  border-radius: 4px;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s;
  position: relative; // For positioning arrow icon

  &:hover {
    background-color: #f5f5f5;
  }

  &.disabled {
    opacity: 0.5;
    cursor: not-allowed;

    &:hover {
      background: transparent;
    }
  }

  &.has-submenu {
    padding-right: 28px; // Make room for the arrow
  }

  i {
    flex-shrink: 0;
  }

  span {
    flex-grow: 1;
    white-space: nowrap;
  }

  .submenu-icon {
    position: absolute;
    right: 8px;
    font-size: 16px;
  }
}

.submenu-container {
  position: absolute;
  top: 0;
  left: 100%; // Position to the right of parent menu
}
</style>
